import { useState, useEffect } from "react";
import api from "@/lib/api";
import { eur, parseAmount, toISO } from "@/lib/format";
import { toast } from "sonner";
import { Delete, Save, RotateCcw, CreditCard, Banknote, Store, ShoppingBag, Wine } from "lucide-react";

const FIELDS = [
  { id: "carte_sur_place", label: "Carte — Sur place", icon: CreditCard, color: "#38BDF8", rate: "12%" },
  { id: "especes_sur_place", label: "Espèces — Sur place", icon: Banknote, color: "#10B981", rate: "12%" },
  { id: "carte_a_emporter", label: "Carte — À emporter", icon: CreditCard, color: "#818CF8", rate: "6%" },
  { id: "especes_a_emporter", label: "Espèces — À emporter", icon: Banknote, color: "#34D399", rate: "6%" },
];

const EMPTY = { carte_sur_place: "", especes_sur_place: "", carte_a_emporter: "", especes_a_emporter: "", boissons_sur_place: "" };

function localCompute(vals) {
  const csp = parseAmount(vals.carte_sur_place);
  const esp = parseAmount(vals.especes_sur_place);
  const cae = parseAmount(vals.carte_a_emporter);
  const eae = parseAmount(vals.especes_a_emporter);
  const sp = csp + esp;
  const ae = cae + eae;
  const boissons = Math.min(parseAmount(vals.boissons_sur_place), sp);
  const food = sp - boissons;
  const tva12 = food - food / 1.12;
  const tva6 = ae - ae / 1.06;
  return {
    total: sp + ae,
    total_carte: csp + cae,
    total_especes: esp + eae,
    total_sur_place: sp,
    total_a_emporter: ae,
    boissons,
    food,
    tva12,
    tva6,
    tva_totale: tva12 + tva6,
  };
}

export default function SalesEntryForm({ sales, reload }) {
  const [date, setDate] = useState(toISO(new Date()));
  const [vals, setVals] = useState(EMPTY);
  const [active, setActive] = useState("carte_sur_place");
  const [saving, setSaving] = useState(false);

  // preload existing entry for the selected date
  useEffect(() => {
    const existing = sales.find((s) => s.date === date);
    if (existing) {
      setVals({
        carte_sur_place: existing.carte_sur_place ? String(existing.carte_sur_place).replace(".", ",") : "",
        especes_sur_place: existing.especes_sur_place ? String(existing.especes_sur_place).replace(".", ",") : "",
        carte_a_emporter: existing.carte_a_emporter ? String(existing.carte_a_emporter).replace(".", ",") : "",
        especes_a_emporter: existing.especes_a_emporter ? String(existing.especes_a_emporter).replace(".", ",") : "",
        boissons_sur_place: existing.boissons_sur_place ? String(existing.boissons_sur_place).replace(".", ",") : "",
      });
    } else {
      setVals(EMPTY);
    }
  }, [date, sales]);

  const c = localCompute(vals);
  const existingForDate = sales.find((s) => s.date === date);

  const setField = (id, v) => setVals((p) => ({ ...p, [id]: v }));

  const keypad = (key) => {
    setVals((p) => {
      let cur = p[active] || "";
      if (key === "back") cur = cur.slice(0, -1);
      else if (key === ",") { if (!cur.includes(",")) cur = (cur || "0") + ","; }
      else if (key === "00") cur = cur === "" || cur === "0" ? "0" : cur + "00";
      else cur = cur === "0" ? key : cur + key;
      return { ...p, [active]: cur };
    });
  };

  const quickAdd = (amount) => {
    setVals((p) => {
      const cur = parseAmount(p[active]);
      const nv = cur + amount;
      return { ...p, [active]: String(nv).replace(".", ",") };
    });
  };

  const reset = () => {
    setVals(EMPTY);
    setActive("carte_sur_place");
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/sales", {
        date,
        carte_sur_place: parseAmount(vals.carte_sur_place),
        especes_sur_place: parseAmount(vals.especes_sur_place),
        carte_a_emporter: parseAmount(vals.carte_a_emporter),
        especes_a_emporter: parseAmount(vals.especes_a_emporter),
        boissons_sur_place: parseAmount(vals.boissons_sur_place),
      });
      const [y, m, d] = date.split("-");
      toast.success(`Ventes du ${d}/${m}/${y} enregistrées`, { description: `Total : ${eur(c.total)}` });
      await reload();
    } catch (e) {
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const keypadKeys = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "00", "0", ","];

  return (
    <div className="max-w-2xl mx-auto vlf-fade-up" data-testid="sales-entry-form">
      <div className="flex items-end justify-between gap-4 mb-5">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold tracking-tight">Clôture de caisse</h2>
          <p className="text-sm text-[#94A3B8]">Saisissez les 4 montants encaissés du jour</p>
        </div>
        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-[#94A3B8] mb-1">Date</label>
          <input
            data-testid="sales-date-input"
            type="date"
            value={date}
            max={toISO(new Date())}
            onChange={(e) => setDate(e.target.value)}
            className="h-12 px-3 rounded-xl bg-[#131920] border border-[#253242] text-white font-mono outline-none focus:border-[#FF5A1F]"
          />
        </div>
      </div>

      {existingForDate && (
        <div className="mb-4 text-sm rounded-lg bg-[#F59E0B]/14 border border-[#F59E0B]/40 text-[#F59E0B] px-3 py-2">
          Une saisie existe déjà pour cette date — l'enregistrement la mettra à jour.
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {FIELDS.map((f) => {
          const Icon = f.icon;
          const isActive = active === f.id;
          return (
            <button
              type="button"
              key={f.id}
              onClick={() => setActive(f.id)}
              className={`text-left rounded-xl p-4 border transition-all active:scale-[0.99] ${
                isActive
                  ? "bg-[#1A222C] border-[#FF5A1F] shadow-[0_0_0_1px_rgba(255,90,31,0.4)]"
                  : "bg-[#131920] border-[#253242] hover:border-[#3A4D66]"
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="flex items-center gap-2 text-sm font-semibold" style={{ color: f.color }}>
                  <Icon className="w-4 h-4" /> {f.label}
                </span>
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#222D3A] text-[#94A3B8]">
                  TVA {f.rate}
                </span>
              </div>
              <input
                data-testid={`input-${f.id.replace(/_/g, "-")}`}
                inputMode="decimal"
                value={vals[f.id]}
                onFocus={() => setActive(f.id)}
                onChange={(e) => setField(f.id, e.target.value.replace(/[^0-9.,]/g, ""))}
                placeholder="0,00"
                className="w-full bg-transparent font-mono text-2xl font-bold text-white outline-none placeholder:text-[#3A4D66]"
              />
            </button>
          );
        })}
      </div>

      {/* Boissons sur place (TVA 21%) */}
      <button
        type="button"
        onClick={() => setActive("boissons_sur_place")}
        className={`w-full text-left rounded-xl p-4 mt-3 border transition-all active:scale-[0.99] ${
          active === "boissons_sur_place"
            ? "bg-[#1A222C] border-[#F472B6] shadow-[0_0_0_1px_rgba(244,114,182,0.4)]"
            : "bg-[#131920] border-[#253242] hover:border-[#3A4D66]"
        }`}
      >
        <div className="flex items-center justify-between mb-2">
          <span className="flex items-center gap-2 text-sm font-semibold text-[#F472B6]">
            <Wine className="w-4 h-4" /> Dont boissons — Sur place
          </span>
          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#222D3A] text-[#94A3B8]">SUIVI</span>
        </div>
        <input
          data-testid="input-boissons-sur-place"
          inputMode="decimal"
          value={vals.boissons_sur_place}
          onFocus={() => setActive("boissons_sur_place")}
          onChange={(e) => setField("boissons_sur_place", e.target.value.replace(/[^0-9.,]/g, ""))}
          placeholder="0,00"
          className="w-full bg-transparent font-mono text-2xl font-bold text-white outline-none placeholder:text-[#3A4D66]"
        />
        <p className="text-xs text-[#64748B] mt-1">
          Montant des boissons vendues sur place — suivi quotidien, sans calcul de TVA.
        </p>
      </button>

      {/* Live totals */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
        <Stat label="Sur place" value={eur(c.total_sur_place)} icon={Store} color="#F97316" />
        <Stat label="À emporter" value={eur(c.total_a_emporter)} icon={ShoppingBag} color="#818CF8" />
        <Stat label="Carte" value={eur(c.total_carte)} icon={CreditCard} color="#38BDF8" />
        <Stat label="Espèces" value={eur(c.total_especes)} icon={Banknote} color="#10B981" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
        <div className="rounded-xl bg-gradient-to-br from-[#FF5A1F] to-[#E04810] p-4 shadow-[0_0_24px_-6px_rgba(255,90,31,0.5)]">
          <div className="text-xs font-bold uppercase tracking-wider text-white/80">Total encaissé du jour</div>
          <div data-testid="live-total-ca-display" className="font-mono text-3xl font-black text-white mt-1">
            {eur(c.total)}
          </div>
        </div>
        <div className="rounded-xl bg-[#131920] border border-[#253242] p-4">
          <div className="text-xs font-bold uppercase tracking-wider text-[#FACC15]">TVA due (12% + 6%)</div>
          <div data-testid="live-tva-total-display" className="font-mono text-3xl font-black text-[#FACC15] mt-1">
            {eur(c.tva_totale)}
          </div>
          <div className="text-xs text-[#94A3B8] mt-1 font-mono">
            12% : {eur(c.tva12)} • 6% : {eur(c.tva6)}
          </div>
        </div>
      </div>

      {/* Numeric keypad */}
      <div className="mt-4 rounded-2xl bg-[#131920]/95 border border-[#253242] p-3">
        <div className="flex gap-2 mb-2 overflow-x-auto pb-1">
          {[5, 10, 20, 50, 100].map((a) => (
            <button
              key={a}
              type="button"
              data-testid={`quick-add-${a}`}
              onClick={() => quickAdd(a)}
              className="shrink-0 h-10 px-3 rounded-lg bg-[#222D3A] border border-[#3A4D66] text-[#F8FAFC] text-sm font-semibold hover:bg-[#2b3849] active:scale-95 transition-all"
            >
              +{a} €
            </button>
          ))}
        </div>
        <div className="grid grid-cols-3 gap-2">
          {keypadKeys.map((k) => (
            <button
              key={k}
              type="button"
              data-testid={`keypad-btn-${k === "," ? "comma" : k}`}
              onClick={() => keypad(k)}
              className="h-14 rounded-xl bg-[#1A222C] border border-[#253242] font-mono text-2xl font-bold text-white hover:bg-[#222D3A] active:scale-95 transition-all"
            >
              {k}
            </button>
          ))}
          <button
            type="button"
            data-testid="keypad-btn-back"
            onClick={() => keypad("back")}
            className="h-14 col-span-3 rounded-xl bg-[#222D3A] border border-[#3A4D66] flex items-center justify-center text-white hover:bg-[#2b3849] active:scale-95 transition-all"
          >
            <Delete className="w-6 h-6" />
          </button>
        </div>
      </div>

      <div className="flex gap-3 mt-4">
        <button
          type="button"
          data-testid="reset-sale-button"
          onClick={reset}
          className="h-14 px-5 rounded-xl bg-[#1A222C] border border-[#253242] text-[#94A3B8] font-semibold flex items-center gap-2 hover:text-white active:scale-[0.98] transition-all"
        >
          <RotateCcw className="w-5 h-5" /> Réinitialiser
        </button>
        <button
          type="button"
          data-testid="submit-sale-button"
          onClick={save}
          disabled={saving || c.total <= 0}
          className="h-14 flex-1 rounded-xl bg-[#FF5A1F] hover:bg-[#E04810] disabled:opacity-50 text-white font-bold text-lg flex items-center justify-center gap-2 active:scale-[0.98] transition-all"
        >
          <Save className="w-5 h-5" /> {existingForDate ? "Mettre à jour" : "Enregistrer la journée"}
        </button>
      </div>
    </div>
  );
}

function Stat({ label, value, icon: Icon, color }) {
  return (
    <div className="rounded-xl bg-[#131920] border border-[#253242] p-3">
      <div className="flex items-center gap-1.5 text-xs font-semibold" style={{ color }}>
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className="font-mono text-lg font-bold text-white mt-1">{value}</div>
    </div>
  );
}
